import type { DateRangePreset } from './types';

/**
 * FE-owned date-range derivation (PRD §6). Each preset collapses to a
 * `{ fromDate, toDate }` pair of ISO `YYYY-MM-DD` dates before the
 * `GET /payments` call; `customerId` and the rest stay server-side concerns.
 *
 * A blank `fromDate` is the contract's "no lower bound" (server defaults it to
 * `1900-01-01`), so `custom` with empty inputs sends nothing.
 *
 * `now` is injectable for deterministic tests; production passes the real date.
 * Dates are computed in UTC so the ISO slice is stable regardless of timezone.
 */

const iso = (d: Date): string => d.toISOString().slice(0, 10);

export type DerivedRange = { fromDate?: string; toDate?: string };

export const deriveRange = (
  preset: DateRangePreset,
  custom: DerivedRange = {},
  now: Date = new Date(),
): DerivedRange => {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();

  switch (preset) {
    case 'current-month':
      return {
        fromDate: iso(new Date(Date.UTC(year, month, 1))),
        toDate: iso(new Date(Date.UTC(year, month + 1, 0))),
      };
    case 'last-month':
      return {
        fromDate: iso(new Date(Date.UTC(year, month - 1, 1))),
        toDate: iso(new Date(Date.UTC(year, month, 0))),
      };
    case 'current-year':
      return {
        fromDate: iso(new Date(Date.UTC(year, 0, 1))),
        toDate: iso(new Date(Date.UTC(year, 11, 31))),
      };
    case 'last-year':
      return {
        fromDate: iso(new Date(Date.UTC(year - 1, 0, 1))),
        toDate: iso(new Date(Date.UTC(year - 1, 11, 31))),
      };
    case 'custom':
      return { fromDate: custom.fromDate || undefined, toDate: custom.toDate || undefined };
    default:
      return {};
  }
};

/** Custom-range validity: a `toDate` must not precede a `fromDate` (PRD §9). */
export const isCustomRangeValid = (fromDate?: string, toDate?: string): boolean => {
  if (!fromDate || !toDate) return true;
  return toDate >= fromDate;
};
